/**
 * Cross-Domain API Client (Sprint 3.6)
 *
 * Provides typed wrappers for cross-entity linking and search.
 */

import { API_BASE } from './api-config';
import { useAuthStore } from '../stores/authStore';

const CROSS_BASE = `${API_BASE}/cross`;

// Auth helper

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = (body as { error?: string }).error ?? `HTTP ${res.status}`;
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

// Types

export interface EmailToTodoPayload {
  emailId: string;
  title?: string;
  priority?: number;
  dueDate?: string;
}

export interface LinkNoteToEmailPayload {
  noteId: string;
  emailId: string;
}

export interface LinkNoteToTodoPayload {
  noteId: string;
  todoId: string;
}

export interface SearchResult {
  id: string;
  type: 'email' | 'todo' | 'note';
  title: string;
  description: string | null;
}

export interface EmailToTodoResponse {
  todoId: string;
  title: string;
}

export interface LinkNoteToEmailResponse {
  success: boolean;
}

export interface LinkNoteToTodoResponse {
  success: boolean;
}

export interface SearchResponse {
  results: SearchResult[];
}

// API client

export const crossDomainApi = {
  /**
   * Create a todo from an email thread.
   * Pre-fills title from email subject, snippet in description.
   */
  emailToTodo(payload: EmailToTodoPayload): Promise<EmailToTodoResponse> {
    return apiFetch<EmailToTodoResponse>(`${CROSS_BASE}/email-to-todo`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Link an existing note to an email thread.
   */
  linkNoteToEmail(payload: LinkNoteToEmailPayload): Promise<LinkNoteToEmailResponse> {
    return apiFetch<LinkNoteToEmailResponse>(`${CROSS_BASE}/note-to-email`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Link an existing note to a todo.
   */
  linkNoteToTodo(payload: LinkNoteToTodoPayload): Promise<LinkNoteToTodoResponse> {
    return apiFetch<LinkNoteToTodoResponse>(`${CROSS_BASE}/note-to-todo`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Search across email, todos, and notes simultaneously.
   * Minimum query length: 2 characters.
   */
  search(q: string): Promise<SearchResponse> {
    return apiFetch<SearchResponse>(`${CROSS_BASE}/search?q=${encodeURIComponent(q)}`);
  },
};
